import { Analytics } from '@cobliteam/analytics';
import type { Path } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useAssignDriverFaceImage } from '@cobliteam/hooks/src/useCamera/useDriverFaceCluster';
import { buildImageAssignRequests } from '@cobliteam/hooks/src/useCamera/useFaceId';
import useFuscaVehicle from '@cobliteam/hooks/src/useEntity/useFuscaVehicles';
import { useTranslation } from '@cobliteam/i18n';
import Alert from '@cobliteam/ui/components/Alert';
import { TableCell } from '@cobliteam/ui/components/Table';
import { SelectDriver } from '@cobliteam/ui-business/components';
import DriverConfirmationDialog from '@cobliteam/ui-business/components/dialogs/DriverConfirmationDialog';
import AssociationHistoryDrawer from '@cobliteam/ui-business/components/drawers/AssociationHistoryDrawer';
import { RawIdentificationSource } from '@cobliteam/ui-business/components/MultiSelects/MultiSelectAssociationSource/MultiSelectAssociationSource';
import type { DriverOption } from '@cobliteam/ui-business/components/Selects/SelectDriver/SelectDriver';
import { formatISO } from '@cobliteam/utils/date';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

interface DriverColumnProps {
  path: Path;
}

const DriverColumn = ({ path }: DriverColumnProps) => {
  const selectedDriver = path.driver
    ? {
        id: path.driver.id!,
        name: path.driver.name!,
      }
    : undefined;

  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [driver, setDriver] = useState<DriverOption | undefined>(selectedDriver);

  const { t } = useTranslation('path-association');
  const { createDriverAssociationRequestBatch } = useFuscaVehicle();
  const queryClient = useQueryClient();
  const { mutateAsync } = createDriverAssociationRequestBatch;
  const { mutateAsync: assignFaceImages } = useAssignDriverFaceImage();

  const handleDriverChange = (driver?: DriverOption) => {
    if (!(driver || path.driver)) return;

    setDriver(driver);
    setOpenConfirmationDialog(true);
  };

  const handleOpenChange = (open: boolean) => {
    setOpenConfirmationDialog(open);

    // Restore to the server-state driver on any close path (Cancel / X / Escape / outside-click)
    // so the picker doesn't end up blank after the user dismisses the confirmation.
    if (!open) {
      setDriver(selectedDriver);
    }
  };

  const handleConfirm = async () => {
    Analytics.track('Path Association - Driver - Confirm');

    try {
      const vehicleId = path.vehicle?.id;
      if (!vehicleId) {
        Alert.error(t('associate_driver_vehicle_not_found'));
        return;
      }

      await mutateAsync({
        vehicleId,
        associationsRequests: [
          {
            driverId: driver?.id,
            startTime: formatISO(path.startTime + 1000), //+ 1 second is used to ensure reference time is after path start
            source: RawIdentificationSource.PATH_REVIEW.valueOf(),
          },
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ['PATH_ASSOCIATION', 'paths'],
      });
      Alert.success(t('associate_driver_success'));

      if (driver?.id) {
        const imageAssignments = buildImageAssignRequests(queryClient, [path], driver.id);
        if (imageAssignments.length > 0) {
          assignFaceImages(imageAssignments).catch(() => {});
        }
      }
    } catch (_error) {
      Alert.error(t('associate_driver_error'));
    } finally {
      setOpenConfirmationDialog(false);
    }
  };

  return (
    <TableCell className='flex flex-col gap-1.5'>
      <SelectDriver value={driver} onChange={handleDriverChange} allowNoDriver />
      <DriverConfirmationDialog
        path={path}
        open={openConfirmationDialog}
        onOpenChange={handleOpenChange}
        newDriver={driver}
        onConfirm={handleConfirm}
        onCancel={() => setDriver(selectedDriver)}
      />
      <AssociationHistoryDrawer path={path} />
    </TableCell>
  );
};

export default DriverColumn;
